<script setup>
import { ref, watch } from 'vue';
import { usePage, router } from '@inertiajs/vue3';

import Footer from '@/Components/Footer.vue';
import TopNavBar from '@/Components/TopNavBar.vue';

defineProps({
    title: String,
    menuItems: Array,
});

// SEO Handling
const page = usePage();

const updateHead = (seo) => {
    if (!seo) return;

    // Set title
    if (seo.title) {
        document.title = seo.title;
    }

    // Remove all previously injected SEO tags
    document.querySelectorAll('[data-seo]').forEach(el => el.remove());

    // Canonical link
    if (seo.canonical) {
        const link = document.createElement('link');
        link.rel = 'canonical';
        link.href = seo.canonical;
        link.setAttribute('data-seo', 'true');
        document.head.appendChild(link);
    }

    // Meta description
    if (seo.description) {
        const metaDesc = document.createElement('meta');
        metaDesc.name = 'description';
        metaDesc.content = seo.description;
        metaDesc.setAttribute('data-seo', 'true');
        document.head.appendChild(metaDesc);
    }

    // Meta keywords
    if (seo.keywords) {
        const metaKeywords = document.createElement('meta');
        metaKeywords.name = 'keywords';
        metaKeywords.content = seo.keywords;
        metaKeywords.setAttribute('data-seo', 'true');
        document.head.appendChild(metaKeywords);
    }

    // Open Graph tags
    if (seo.openGraph) {
        Object.entries(seo.openGraph).forEach(([key, value]) => {
            const og = document.createElement('meta');
            og.setAttribute('property', `og:${key}`);
            og.content = value;
            og.setAttribute('data-seo', 'true');
            document.head.appendChild(og);
        });
    }

    // Structured data (JSON-LD)
    if (seo.structuredData && Array.isArray(seo.structuredData)) {
        seo.structuredData.forEach(schema => {
            const script = document.createElement('script');
            script.type = 'application/ld+json';
            script.textContent = JSON.stringify(schema);
            script.setAttribute('data-seo', 'true');
            document.head.appendChild(script);
        });
    }
};

// Safely watch seo prop
watch(
  () => page?.props?.value?.seo,
  (seo) => {
    if (seo) updateHead(seo);
  },
  { immediate: true }
);
</script>

<template>
    <div class="site-wrapper">
        <div class="grid grid-cols-1">
            <TopNavBar />
        </div>

        <div class="site-content bg-gray-100">
            <!-- Page Heading -->
            <header v-if="$slots.header" class="bg-white shadow">
                <div class="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                    <slot name="header" />
                </div>
            </header>

            <!-- Page Content -->
            <main>
                <slot />
            </main>
        </div>

        <!-- Footer Component -->
        <Footer />
    </div>
</template>

<style>
.site-wrapper {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
}

.site-content {
    flex: 1;
}
</style>
